import { Divider } from '@mui/material';
import * as React from 'react';
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { Outlet } from 'react-router-dom';
import { useCustomSearchParams } from 'common/hooks/customSearchParams/customSearchParams';
import { fetchWatchList, getWatchListCount, WatchList } from 'services/clients/watchList';
import { SelectedContainerContextProvider } from 'pages/TrackAndTrace/contexts/SelectedContainerContextProvider';
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { useNavHeightContext } from 'common/contexts/NavHeightContext';
import { useSegment } from 'common/hooks/segment';
import { fetchShippingLines, ShippingLineQueryKeys } from 'services/clients/shippingLine';
import { ContainerTypeQueryKeys, fetchContainerTypes } from 'services/clients/containerType';
import { fetchTerminals } from 'services/clients/terminal';
import { useRegion } from 'common/hooks/region';
import { PortCollectionFeature } from 'types/Port';
import { SelectionContextProvider } from 'common/contexts/SelectionContext';
import { AddContainers, ComingSoonBanner, ContainerTable, Controls } from './components';
import { WATCH_LIST_QUERY_PARAMS } from './constants';
import styles from '../../styles/typography.module.css';
import { CONTAINER_SEARCH_LIMIT, REFRESH_INTERVAL_MS } from './config';
import { anyFiltersOn, filtersToBoolean } from './components/controls/filters/utils';

interface PromptState {
  show: boolean;
  containers: string[];
}

/**
 * The Track & Trace (/container) page empowers customers to quickly add their
 * containers and let BlueCargo do the heavy lifting.
 * https://www.notion.so/join-bluecargo-ship/Track-Trace-03b5e6199d88449b9db04c181264f743
 */
export const TrackAndTrace: React.FC = () => {
  const { track } = useSegment();
  const region = useRegion();

  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState<GridColumnVisibilityModel>({
      containerNumber: true,
      state: true,
      holds: true,
      lastFreeDay: true,
      pickup: true,
      emptyReturn: true,
      detention: true,
      dualReturns: true,
      customerTag: true,
    });

  /** Keep track of the containers we last prompted for as a string (changes any time we change the search query) */
  const lastPrompt = React.useRef<string>('');
  /** Keep track of whether we prompted for no containers (only once, ever) */
  const hasPromptedForEmpty = React.useRef<boolean>(false);

  /** Whether to show the add containers prompt and any containers to prepopulate it with */
  const [promptState, setPromptState] = React.useState<PromptState>({
    show: false,
    containers: [],
  });

  /**
   * When the addContainers param is in the url, we want to open the modal and
   * remove it from the params to prevent reopening
   */
  React.useEffect(() => {
    if (searchParams.addContainers) {
      setSearchParams({ addContainers: null }, true);
      setPromptState({ show: true, containers: [] });
    }
  }, [searchParams.addContainers, setSearchParams]);

  const queryClient = useQueryClient();

  // Just to avoid loading state in filters
  useQuery([ShippingLineQueryKeys.ShippingLines], ({ signal }) => fetchShippingLines({ signal }), {
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  useQuery(
    [ContainerTypeQueryKeys.ContainerTypes],
    ({ signal }) => fetchContainerTypes({ signal }),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );
  useQuery(
    ['terminals', 'TrackNTrace'],
    ({ signal }) => fetchTerminals({ signal, onlyTrackAndTrace: true }),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const watchListQuery: UseQueryResult<WatchList> = useQuery(
    ['watchList', searchParams],
    ({ signal }) => fetchWatchList({ filters: { ...searchParams, unwatched: false }, signal }),
    {
      refetchInterval: REFRESH_INTERVAL_MS,
      onSuccess: (data) => {
        if (data && !watchListQuery.isRefetching && searchParams.containerNumbers.length > 0) {
          const missingWatch = data.missingWatch || [];
          track('Container Searched', {
            allContainersInTheWatchList: missingWatch.length === 0,
            missingContainers: missingWatch,
            nbMissingContainers: missingWatch.length,
            containerSearched: searchParams.containerNumbers,
            numberOfContainerSearched: searchParams.containerNumbers.length,
          });
        }
      },
    }
  );

  const watchListCountQuery = useQuery(
    ['watchListCount', searchParams],
    ({ signal }) => getWatchListCount({ filters: { ...searchParams, unwatched: false }, signal }),
    {
      refetchInterval: REFRESH_INTERVAL_MS,
      onSuccess: (data) => {
        if (data && !watchListCountQuery.isRefetching) {
          track(
            'Container Filter Selected',
            {
              totalWatchedContainers: data.totalWatched,
              nbGatedOutContainers: data.currentGatedOut,
              nbTerminatedContainers: data.currentTerminated,
              nbOnVesselContainers: data.currentOnVessel,
              nbArrivedContainers: data.currentImport,
              nbNotFoundContainers: data.currentNotFound,
              filters: filtersToBoolean(searchParams),
            },
            { includeUser: true }
          );
        }
      },
    }
  );

  const { marginTop } = useNavHeightContext();

  React.useEffect(() => {
    const watchListResults = watchListQuery.data;
    if (watchListResults?.missingWatch && watchListResults.missingWatch.length > 0) {
      const sortedContainerNumbers = _(watchListResults.missingWatch)
        .sort(
          (a, b) =>
            searchParams.containerNumbers.indexOf(a) - searchParams.containerNumbers.indexOf(b)
        )
        .value();

      // Not really hashed, but acting as one
      const hashedContainers = JSON.stringify(sortedContainerNumbers);

      // If we just prompted for this list of containers, don't do it again
      if (lastPrompt.current !== hashedContainers) {
        lastPrompt.current = hashedContainers;
        setPromptState({ show: true, containers: sortedContainerNumbers });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchListQuery.data, searchParams.containerNumbers]);

  React.useEffect(() => {
    if (searchParams.containerNumbers.length === 0) {
      lastPrompt.current = '';
    }
  }, [searchParams.containerNumbers]);

  /**
   * This effect shows the add containers modal if the user has no containers
   */
  React.useEffect(() => {
    if (
      watchListQuery.status === 'success' &&
      watchListQuery.data?.count === 0 &&
      // Distinguish between a search and no search
      (!watchListQuery.data.missingWatch || watchListQuery.data.missingWatch.length === 0) &&
      !anyFiltersOn(searchParams) &&
      !hasPromptedForEmpty.current
    ) {
      hasPromptedForEmpty.current = true;
      setPromptState({ show: true, containers: [] });
    }
    // This was deliberate so that this effect only runs when the query status changes; otherwise the
    // prompt would appear more often that we'd like
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchListQuery.status]);

  const handleAddContainersModalOnClose = () => {
    setPromptState({ show: false, containers: [] });
  };

  const handleOpenAddContainersModal = (containers?: string[]) => {
    setPromptState({ show: true, containers: containers || [] });
  };

  const showComingSoon = !region.features?.includes(PortCollectionFeature.TrackAndTrace);

  return (
    <SelectionContextProvider>
      <SelectedContainerContextProvider>
        <div style={{ top: marginTop }}>
          <Outlet />
        </div>
        <div className={`${styles.font}`}>
          <div className="px-8 pt-4 pb-4 flex items-start flex-col space-y-4">
            {showComingSoon ? <ComingSoonBanner regionLabel={region.name} /> : null}
            <h1 className="pt-4">Track &amp; Trace Containers</h1>
            <AddContainers
              forceShow={promptState.show}
              prepopulatedContainers={promptState.containers}
              // Clear prepopulated containers on close so they're not visible next time
              onClose={handleAddContainersModalOnClose}
              onTrackAndTrace={(option) => {
                if (option.method === 'input') {
                  const newContainerNumbers = _.takeRight(
                    [...new Set([...option.numbers, ...searchParams.containerNumbers])],
                    CONTAINER_SEARCH_LIMIT
                  );
                  track(
                    'Container Added',
                    {
                      source: 'input_field',
                      containers: option.numbers,
                      nb_containers: option.numbers.length,
                    },
                    { includeUser: true }
                  );
                  setSearchParams(
                    {
                      containerNumbers: newContainerNumbers,
                    },
                    false
                  );
                }
                queryClient.invalidateQueries(['watchList']);
                queryClient.invalidateQueries(['watchListCount']);
              }}
            />
            <Divider className="w-full border-blue-300" />
            <Controls
              filterCounts={watchListCountQuery.data}
              onColumnVisibilityChange={setColumnVisibilityModel}
              columnVisibilityModel={columnVisibilityModel}
            />
          </div>
          <ContainerTable
            watchListQuery={watchListQuery}
            openAddContainersModal={() => handleOpenAddContainersModal()}
            columnVisibilityModel={columnVisibilityModel}
          />
        </div>
      </SelectedContainerContextProvider>
    </SelectionContextProvider>
  );
};

TrackAndTrace.displayName = 'TrackAndTrace';
